import { RectButton } from "@/client/ui/RectButton";
import { ScopedVariables } from "@/client/ui/ScopedVariables";
import { useWebsiteStore } from "@/client/website";
import { useIntersectionObserver } from "@uidotdev/usehooks";
import { useEffect } from "react";
import { Text } from "@/client/ui/Text";
import { View } from "@/client/ui/View";

export default function WebsitePage() {
  const setIsBodyGetStartedVisible = useWebsiteStore(
    (s) => s.setIsBodyGetStartedVisible,
  );
  const [ref, entry] = useIntersectionObserver({
    threshold: 0.5,
  });

  useEffect(() => {
    setIsBodyGetStartedVisible(entry?.isIntersecting !== false);
  }, [entry?.isIntersecting, setIsBodyGetStartedVisible]);

  return (
    <>
      <View
        className={`www-px-comfortable mb-25 h-screen items-center justify-center pt-25`}
      >
        <View className="mb-25 items-center">
          <Text
            className={`
              www-text-hero text-center

              lg:text-left
            `}
          >
            Learn to read Chinese
          </Text>
        </View>

        <View className="w-[350px] items-stretch gap-2">
          <ScopedVariables variables={{ "--color-fg": `var(--color-cyanold)` }}>
            <RectButton
              href="/learn"
              variant="filled"
              ref={(el) => {
                //  RectButton is a <View> rather than a DOM element.
                ref(el as Element | null);
              }}
            >
              Get Started
            </RectButton>
          </ScopedVariables>

          <RectButton href="/learn" variant="outline">
            I already have an account
          </RectButton>
        </View>
      </View>

      <View className="www-px-comfortable min-h-125 w-full pt-25">
        <View className="max-w-www-col gap-40 self-center">
          <View>
            <Text className="www-text-subtitle">Chinese characters</Text>
            <Text className="www-text-title mb-4">
              Crack the code of Chinese characters, one piece at a time.
            </Text>

            <Text className="www-text-body">
              Each character is a puzzle made up of visual building blocks and
              sounds. Instead of overwhelming you with entire characters, we
              break them down to their core elements—helping you grasp the
              meaning, structure, and pronunciation before putting it all
              together.
            </Text>
          </View>

          <View>
            <Text className="www-text-subtitle">Radicals</Text>
            <Text className="www-text-title mb-4">
              Recognize hundreds of radicals.
            </Text>

            <Text className="www-text-body">
              Each radical carries meaning, and we make them unforgettable by
              turning them into stories. By linking each radical to a vivid
              narrative, you’ll recognize and recall them effortlessly—building
              a solid base for mastering Chinese characters.
            </Text>
          </View>

          <View>
            <Text className="www-text-subtitle">Pinyin</Text>
            <Text className="www-text-title mb-4">
              Turn pinyin into second nature.
            </Text>

            <Text className="www-text-body">
              Every Chinese character is spoken as a single syllable, made up of
              an initial, a final, and a tone. We break these down and assign
              easy-to-remember names to each part, making pronunciation
              effortless and intuitive.
            </Text>
          </View>

          <View>
            <Text className="www-text-subtitle">Mnemonics</Text>
            <Text className="www-text-title mb-4">
              Make Chinese characters unforgettable.
            </Text>

            <Text className="www-text-body">
              Each character comes with a handcrafted story that ties together
              its components, pronunciation, and meaning. By turning abstract
              strokes into vivid narratives, we help you see, hear, and remember
              every character effortlessly—so learning feels natural and recall
              becomes instant.
            </Text>
          </View>

          <View>
            <Text className="www-text-subtitle">Your own pace</Text>
            <Text className="www-text-title mb-4">
              Master Chinese characters in the most efficient order.
            </Text>

            <Text className="www-text-body">
              Our curriculum is built from an in-depth analysis of thousands of
              characters, ensuring you learn them in the most logical and
              effective sequence. Covering every character in the HSK Chinese
              Proficiency Test, this method fast-tracks your
              understanding—helping you read basic sentences quickly and, by the
              end, recognize nearly every character in a newspaper.
            </Text>
          </View>
        </View>
      </View>
    </>
  );
}
